import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/marketing/Header';
import Seo, { organizationSchema } from '../components/Seo';
import Footer from '../components/marketing/Footer';
import TalkToAlfred from '../components/TalkToAlfred';
import ChatWithAlfred from '../components/ChatWithAlfred';
import {
  DEMO_PHONE_DISPLAY,
  useMarketingPage,
  Hero,
  Section,
  SectionHeading,
  CardGrid,
  DarkStatement,
  Steps,
  MathTable,
  FAQ,
  FinalCTA
} from '../components/marketing/sections';

/**
 * Homepage — the GENERIC pitch.
 *
 * History worth knowing before editing: from 2026-07-27 this page WAS the
 * transportation vertical (driver recruiting for carriers). On 2026-08-30 that
 * content moved wholesale to /transportation and this page went back to selling
 * the capability — inbound and outbound calling — with an "areas we cover" grid
 * that hands the visitor off to the vertical page that matches them.
 *
 * Two rules this page exists to hold:
 *   1. It sells the SOFTWARE, not one industry. No carrier/driver copy here —
 *      that belongs on /transportation.
 *   2. Every tile in AREAS points at a page that actually exists and is in
 *      public-routes.mjs. A tile pointing at a redirect is a dead end that
 *      leaks the visitor back to this page.
 *
 * KNOWN CROSS-SESSION DEPENDENCY — the chat widget. AlfredVoice-v2's
 * `core/src/routes/alfredChat.js` BUYER_BLOCK still tells the model "assume a
 * trucking CARRIER or a FREIGHT BROKERAGE", which was correct when this page
 * was transportation-only and is now too narrow for it. The greeting below is
 * deliberately industry-neutral so the widget doesn't open by guessing wrong,
 * but the persona behind it needs to widen before this page's chat is fully
 * consistent. That file is the backend session's, not this one's.
 */

// Neutral opener. The transportation-specific default in ChatWithAlfred stays
// where it belongs — on /transportation.
const CHAT_GREETING =
  "Hi — I'm Alfred, an AI. I make and take calls for businesses: answering every "
  + 'inbound call around the clock, and calling out on the lists nobody has time '
  + 'to work. What do your calls look like right now?';

const HERO_STATS = [
  { value: 'In + Out', label: 'Inbound and outbound calling' },
  { value: 'Instant', label: 'Answered, 24/7' },
  { value: 'Multi', label: 'Agents on one line' },
  { value: 'Days', label: 'To go live' }
];

const CAPABILITIES = [
  {
    title: 'Inbound and outbound',
    description:
      'Most voice AI only answers. Alfred also picks up the phone — calling new leads back in under a minute and working the lists already sitting in your system, the ones nobody has hours for.'
  },
  {
    title: 'Answers immediately, 24/7',
    description:
      'No hold, no queue, no voicemail. Nights, weekends, and holidays included, and the hundredth simultaneous call is answered as fast as the first.'
  },
  {
    title: 'Multiple agents on one line',
    description:
      'One number, several specialized agents behind it. Reception, screening, booking, and follow-up each run as their own agent with their own script, and the call goes to whichever one the caller actually needs.'
  },
  {
    title: 'Genuinely intelligent',
    description:
      'Not a phone tree. Alfred handles accents, background noise, interruptions and changes of subject, asks its own follow-up questions, and knows your business — your hours, your services, your rules.'
  },
  {
    title: 'Compliance built in',
    description:
      'Alfred says it is AI on every call. Do-not-call and opt-out are honored immediately and permanently, and every call is recorded, transcribed, and logged with its consent and disclosure.'
  }
];

/**
 * The areas we cover. Every href here MUST be a live page listed in
 * frontend/public-routes.mjs — see the rule in the header comment.
 */
const AREAS = [
  {
    name: 'Transportation & Carriers',
    to: '/transportation',
    blurb: 'Every driver applicant called in 60 seconds, plus the dead applicant list your recruiters never got back to.'
  },
  {
    name: 'Dispatch Services',
    to: '/industries/dispatchers',
    blurb: 'Driver check-ins, load assignments and after-hours dispatch, handled around the clock.'
  },
  {
    name: 'Freight Brokerages',
    to: '/industries/brokers',
    blurb: 'Carrier coordination, check calls and track-and-trace, without a night desk.'
  },
  {
    name: 'Pest Control',
    to: '/industries/pest-control',
    blurb: 'Books treatments by route density, converts one-time jobs to recurring, and chases no-shows.'
  },
  {
    name: 'Property Management',
    to: '/industries/property-management',
    blurb: 'Triages maintenance, answers tenant questions, and escalates real emergencies at 2am.'
  },
  {
    name: 'Call Centers',
    to: '/industries/call-centers',
    blurb: 'Overflow and after-hours coverage, with callers pre-qualified before an agent picks up.'
  }
];

const STEPS = [
  { title: 'Discovery call', description: '30 minutes on your calls — what comes in, what should go out, and how your team handles both today.' },
  { title: 'We build and test it', description: 'Configured on your real call flows and your real criteria. You listen until it sounds right.' },
  { title: 'Pilot', description: 'A small, bounded run with a full report on every call, so you judge it on results rather than promises.' },
  { title: 'Go live', description: 'Forward your number. Alfred answers, calls out, and keeps going — no new hardware.' }
];

const FAQ_ITEMS = [
  {
    q: 'What is the difference between the inbound and outbound side?',
    a: 'Inbound is Alfred answering your phone — 24/7, instantly, booking and routing and taking messages. Outbound is Alfred placing the calls: ringing a new lead back within a minute, and working through a list of existing records that your team never has time to get to. Most voice AI does only the first. The second is where the work nobody is doing actually lives.'
  },
  {
    q: 'What does "multiple agents on one line" mean?',
    a: 'One phone number can sit in front of several agents, each configured for a different job — a front-desk agent, a screening agent, a booking agent, a follow-up agent. The caller does not pick from a menu; Alfred works out what they want and the right agent takes it from there. It also means one line can serve several departments or locations without extra numbers.'
  },
  {
    q: 'Does Alfred tell people it is AI?',
    a: 'Yes, on every call, without exception. No cloned voices and no pretending to be a person.'
  },
  {
    q: 'Will it hold up against accents, noise and interruptions?',
    a: `That is the question that decides it, so don't take our word for it. Call ${DEMO_PHONE_DISPLAY}, or drop your number above and have Alfred call you, and judge it against the people you actually talk to.`
  },
  {
    q: 'Does it work with my number and the tools I already use?',
    a: 'Yes. You forward your existing number — no new hardware — and Alfred books into the scheduler, CRM or TMS you already run. Every call is summarized to your team by email or text.'
  },
  {
    q: 'How long until it is live?',
    a: 'Days, not months. A short discovery call, then we build and test on your real call flows, then you go live. It is done-for-you: there is no platform for you to learn.'
  },
  {
    q: 'My industry is not in your list. Can you still help?',
    a: 'Usually, yes. The system is not industry-specific — the areas below are simply the ones we have already built and tuned pages for. If your business runs on the phone, call the demo line and tell us what your calls look like.'
  },
  {
    q: 'What happens to my data?',
    a: 'Your data stays yours and we never sell it. We share it only with the service providers who run Alfred — telephony, cloud and AI vendors — under confidentiality obligations. Recordings and transcripts are retained and available to you. Full detail is in our privacy policy.'
  }
];

const HomePage = () => {
  useMarketingPage();

  return (
    <div className="min-h-screen bg-paper text-ink">
      <Seo
        title="Alfred Voice — Inbound and Outbound Voice AI for Every Business Call"
        description="Alfred answers every inbound call instantly, 24/7, and makes the outbound calls your team never gets to. Multiple agents on one line, genuinely intelligent, live in days."
        path="/"
        structuredData={[organizationSchema()]}
      />
      <Header />

      <Hero
        eyebrow="Inbound & Outbound Voice AI · Houston"
        title="Every call your business takes. Every call it never makes."
        secondaryHref="#talk-to-alfred"
        secondaryLabel="Have Alfred call me"
        footnote="Alfred says it’s AI on every call · done-for-you · live in days, not months"
        stats={HERO_STATS}
      >
        Alfred answers your phone the instant it rings, around the clock — and picks it up to work
        the lists and leads nobody on your team has hours for.
      </Hero>

      {/* What it does — the capability pitch, five tiles */}
      <Section id="capabilities" tone="paper">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeading
            eyebrow="What Alfred does"
            title="A phone system that works both directions."
            subtitle="Five things that separate it from an answering service and from the AI receptionists you have already been pitched."
          />
          <CardGrid items={CAPABILITIES} tone="paper" />
        </div>
      </Section>

      {/* Hear Alfred — the strongest asset, kept high on the page */}
      <Section id="talk-to-alfred" tone="warm">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeading
            eyebrow="Hear it for yourself"
            title="Don't take our word for it."
            subtitle={`Pick the call, drop your number, and Alfred rings you in about a minute from ${DEMO_PHONE_DISPLAY}.`}
          />
          <TalkToAlfred showHeading={false} />
        </div>
      </Section>

      {/* The one dark moment on this page */}
      <DarkStatement
        eyebrow="The difference"
        title="Most voice AI waits for the phone to ring."
        closer="Alfred also picks it up."
        tone="paper"
      >
        <p>
          Answering the phone is the easy half, and everyone sells it. The hard half is the list —
          the lead who enquired at midnight, the customer who lapsed, the applicant who went cold.
          That work is economically impossible for a human team, which is why it never gets done.
        </p>
      </DarkStatement>

      {/* Areas we cover — every tile is a live page */}
      <Section id="areas" tone="warm">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeading
            eyebrow="Areas we cover"
            title="Built and tuned for the way your industry actually calls."
            subtitle="The capability is the same everywhere. The scripts, the screening criteria and the software it plugs into are not."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {AREAS.map(({ name, to, blurb }) => (
              <Link
                key={to}
                to={to}
                className="fade-in group bg-paper border border-hair rounded-brand p-6 flex flex-col transition-colors hover:border-brass"
              >
                <h3 className="text-lg font-bold mb-2 group-hover:text-brass transition-colors">{name}</h3>
                <p className="text-slateink text-sm font-light leading-relaxed flex-1">{blurb}</p>
                <span className="mt-4 text-xs font-semibold uppercase tracking-[0.1em] text-brass">
                  See the page →
                </span>
              </Link>
            ))}
          </div>
          <p className="mt-10 text-center text-slateink font-light max-w-2xl mx-auto">
            Not on the list? Alfred isn’t industry-specific — these are just the areas we’ve already
            built for. Call {DEMO_PHONE_DISPLAY} and tell us what your calls look like.
          </p>
        </div>
      </Section>

      {/* Pricing — the header links here, so it has to exist on this page */}
      <Section id="pricing" tone="paper">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading
            eyebrow="Month-to-month"
            title="What it costs"
            subtitle="Where you start depends on which side you need first. Full pricing is on each area page."
          />
          <MathTable
            tone="paper"
            highlightLast={false}
            rows={[
              ['Outbound pilot', '$995 — 500 records, one week, full report'],
              ['Inbound plans', 'from $149/mo'],
              ['Carrier dispatch', 'from $499/mo'],
              ['Brokerage dispatch', 'from $799/mo']
            ]}
          />
          <p className="mt-6 text-slateink font-light">
            Month-to-month, no long-term contract. One-time setup varies by plan. The pilot is
            deliberately small enough to approve today and judge on its own results.
          </p>
        </div>
      </Section>

      <Steps steps={STEPS} tone="warm" />

      <FAQ items={FAQ_ITEMS} tone="paper" />

      <FinalCTA
        heading="Somebody is calling your business right now."
        body="Hear what Alfred sounds like before you decide anything else."
      />

      <Footer />

      <ChatWithAlfred source="landing-home" callNumber={DEMO_PHONE_DISPLAY} greeting={CHAT_GREETING} />
    </div>
  );
};

export default HomePage;
